/**
 * fetchAllForExport — helper canonique pour recuperer toutes les lignes
 * d'une liste paginee server-side avant un export.
 *
 * Probleme : par defaut, le DataGrid exporte seulement `data` (la page
 * actuelle, typiquement 20-100 lignes). Si l'utilisateur a applique un
 * filtre date qui ramene 5000 lignes en backend, l'export silencieux ne
 * contiendrait que les 100 premieres -> incident classique signale par les
 * clients.
 *
 * Solution : un seul fetch avec `limit = EXPORT_LIMIT_MAX`, puis on compare
 * `pagination.total` au nombre de lignes recues :
 *  - egal -> tout tient, l'export procede normalement.
 *  - total > recus -> trop de lignes, on throw `ExportTooLargeError` et le
 *    DataGrid affiche un toast "Affinez vos filtres" plutot que de produire
 *    un export tronque silencieusement.
 *
 * Usage page :
 *   exportable={{
 *     fileName: '...',
 *     onFetchAllRows: () =>
 *       fetchAllForExport(({ limit }) => myService.findMany({ ...filters, limit, page: 1 })),
 *   }}
 */

/**
 * Shape minimale du retour d'une API de listing paginee.
 *
 * Le backend bstock standardise sur `{ items, pagination: { total } }`
 * (cf controllers list / findAll). On accepte aussi `{ data, total }` au
 * cas ou un endpoint utilise un autre format historique.
 */
export interface PaginatedResponse<T> {
  items: T[];
  pagination: { total: number };
}

/** Erreur levee quand le total depasse `EXPORT_LIMIT_MAX`. */
export class ExportTooLargeError extends Error {
  readonly total: number;
  readonly limit: number;
  constructor(total: number, limit: number) {
    super(
      `Trop de lignes a exporter : ${total.toLocaleString('fr-FR')} (limite : ${limit.toLocaleString('fr-FR')}). Affinez les filtres.`
    );
    this.name = 'ExportTooLargeError';
    this.total = total;
    this.limit = limit;
  }
}

export interface FetchAllForExportOptions {
  /**
   * Borne haute envoyee au backend (et borne haute du test "tout tient").
   * Par defaut : `EXPORT_LIMIT_MAX` (10 000) — utilise la constante depuis
   * `@bstock/shared` cote consumer si vous voulez ajuster.
   */
  limit?: number;
}

const DEFAULT_EXPORT_LIMIT = 10_000;

/**
 * Lance un fetch unique avec `limit = limit ?? 10000`, valide que le total
 * ne depasse pas la limite, et retourne les lignes pretes pour l'export.
 *
 * @param fetcher Fonction qui execute la requete avec les options fournies
 *   (la page doit injecter ses filtres dans le service avant d'appeler).
 * @param options.limit Override de la borne haute. Defaut : 10 000.
 * @throws {ExportTooLargeError} si `pagination.total > items.length`.
 */
export async function fetchAllForExport<T>(
  fetcher: (options: { limit: number }) => Promise<PaginatedResponse<T>>,
  options: FetchAllForExportOptions = {}
): Promise<T[]> {
  const limit = options.limit ?? DEFAULT_EXPORT_LIMIT;
  const data = await fetcher({ limit });
  if (data.pagination.total > data.items.length) {
    throw new ExportTooLargeError(data.pagination.total, limit);
  }
  return data.items;
}
